import { ClickAwayListener } from "@mui/base";
import { IconButton, Stack, useForkRef } from "@mui/material";
import { MdsDelete } from "@sinch/icons";
import type { ReactElement } from "react";
import React, { useCallback, useRef, useState } from "react";
import { useWatchDependency } from "@sinch/hooks/utilities/useWatchDependency";
import { EditableBox } from "@sinch/components/form/QuickEditText/EditableBox";
import { QuickEditInput } from "@sinch/components/form/QuickEditText/QuickEditInput";
import { FormControlWrapper, MdsIcon } from "@sinch/components/index";

export interface QuickEditTextProps {
  onChange?: (value: string) => void;
  value?: string;
  maxLength?: number;
  children: ReactElement<{ ref?: React.Ref<HTMLElement> }>;
  onBlur?: () => void;
  disabled?: boolean;
}

interface CapturedChildState {
  className: string;
  style: string;
}

/**
 * A React functional component that provides inline text editing functionality.
 * It allows toggling between a non-editable text display and an input field for editing the text.
 * The component handles child validation to ensure proper usage and supports controlled behavior by consuming `value` and `onChange` props.
 *
 */
export const QuickEditText = ({ children, onBlur, onChange, value, maxLength, disabled }: QuickEditTextProps) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [capturedChildState, setCapturedChildState] = useState<CapturedChildState | null>(null);
  const [inputValue, setInputValue] = useState<string>(value ?? "");
  const childRef = useRef<HTMLElement>(null);
  const combinedRef = useForkRef(childRef, children.props.ref);

  const validateAndSetChildRef = useCallback(
    (node: HTMLElement | null) => {
      if (node) {
        if (
          node.childNodes.length > 1 ||
          (node.childNodes.length === 1 && node.childNodes[0].nodeType !== Node.TEXT_NODE)
        ) {
          throw new Error("QuickEditText only supports a single direct text node as its child's content.");
        }
        combinedRef?.(node);
      } else {
        combinedRef?.(null);
      }
    },
    [combinedRef]
  );

  const enableEditing = useCallback(() => {
    if (disabled) {
      return;
    }
    if (childRef.current) {
      setCapturedChildState({
        className: childRef.current.className,
        style: childRef.current.style.cssText,
      });
      setIsEditing(true);
    }
  }, []);

  const disableEditing = useCallback(() => {
    setIsEditing(false);
    onChange?.(inputValue ?? "");
    onBlur?.();
  }, [onChange, inputValue]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
    },
    [setInputValue]
  );

  const handleClearInput = useCallback(() => {
    setInputValue("");
    onChange?.("");
    setIsEditing(false);
  }, [onChange]);

  const focusAndStyleInput = useCallback(
    (inputElement: HTMLInputElement | null) => {
      if (inputElement) {
        if (capturedChildState) {
          inputElement.style.cssText = capturedChildState.style;
        }
        inputElement.focus();
      }
    },
    [capturedChildState]
  );

  useWatchDependency(() => {
    setInputValue(value ?? "");
  }, [value]);

  return (
    <>
      {!isEditing && (
        <EditableBox onClick={enableEditing} sx={{ width: "fit-content", display: "flex" }}>
          {React.cloneElement(children, { ref: validateAndSetChildRef })}
        </EditableBox>
      )}
      {isEditing && capturedChildState && (
        <ClickAwayListener onClickAway={disableEditing}>
          <Stack alignItems="center" direction="row">
            <FormControlWrapper
              fullWidth={false}
              sx={{
                maxWidth: "calc(100% - 32px)",
              }}
            >
              <QuickEditInput
                ref={focusAndStyleInput}
                className={capturedChildState.className}
                maxLength={maxLength}
                onChange={handleInputChange}
                size={inputValue.length || 1} // Ensure size is at least 1 to be visible
                value={inputValue}
              />
            </FormControlWrapper>
            <IconButton aria-label="Clear input" onClick={handleClearInput} sx={{ p: 0.5, ml: 1 }}>
              <MdsIcon color="error" fontSize="xs" icon={MdsDelete} />
            </IconButton>
          </Stack>
        </ClickAwayListener>
      )}
    </>
  );
};
